/* global URL */
export function normalizeBookingPhone(value) {
  const compact = value.trim().replace(/[\s().-]/g, '');
  if (/^\+91[6-9]\d{9}$/.test(compact)) return compact;
  const national = compact.match(/^(?:\+?1)?([2-9]\d{2}[2-9]\d{6})$/)?.[1];
  return national ? `+1${national}` : '';
}

export const dateKey = (value, timezone) => new Intl.DateTimeFormat('en-CA', {
  timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date(value));

export function calendarDays(timezone, count = 28, now = new Date()) {
  const today = dateKey(now, timezone);
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(`${today}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() + index);
    return { key: date.toISOString().slice(0, 10), date, weekday: date.getUTCDay() };
  });
}

export function availableClassDates(classes, timezone) {
  return new Set(classes.filter(item => item.spotsLeft > 0).map(item => dateKey(item.start, timezone)));
}

export function indexClassesByDate(classes, timezone) {
  const format = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
  });
  const dates = new Map();
  for (const item of classes) {
    const key = format.format(new Date(item.start));
    if (!dates.has(key)) dates.set(key, { classes: [], available: false });
    const day = dates.get(key);
    day.classes.push(item);
    day.available ||= item.spotsLeft > 0;
  }
  return dates;
}

export function hasMultipleAudiences(classes) {
  return new Set(classes.map(item => item.audience ?? 'all')).size > 1;
}

export function resolveProgrammeFilter(classes, selection) {
  if (selection.startsWith('service:')) return classes.some(item => item.serviceId === selection.slice(8)) ? selection : '';
  if (selection.startsWith('class:')) return classes.some(item => item.className === selection.slice(6)) ? selection : '';
  return '';
}

export function groupClassesByTime(classes, timezone) {
  const hour = new Intl.DateTimeFormat('en-GB', { timeZone: timezone, hour: 'numeric', hourCycle: 'h23' });
  const groups = new Map();
  for (const item of [...classes].sort((a, b) => new Date(a.start) - new Date(b.start))) {
    const localHour = Number(hour.format(new Date(item.start)));
    const label = localHour < 12 ? 'Morning' : localHour < 17 ? 'Afternoon' : 'Evening';
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(item);
  }
  return [...groups].map(([label, items]) => ({ label, items }));
}

export function brandColors(color) {
  // Configure App accepts a color string. Only bounded hex values become CSS.
  const short = /^#([0-9a-f]{3})$/i.exec(color || '');
  const normalized = short ? `#${short[1].split('').map(c => c + c).join('')}` : color;
  const accent = /^#[0-9a-f]{6}$/i.test(normalized || '') ? normalized : '#0f766e';
  const rgb = accent.slice(1).match(/../g).map(v => Number.parseInt(v, 16) / 255)
    .map(v => v <= .04045 ? v / 12.92 : ((v + .055) / 1.055) ** 2.4);
  const luminance = rgb[0] * .2126 + rgb[1] * .7152 + rgb[2] * .0722;
  const whiteContrast = 1.05 / (luminance + .05);
  const blackContrast = (luminance + .05) / .05;
  return { accent, onAccent: whiteContrast >= blackContrast ? '#ffffff' : '#000000' };
}

export function safeLogo(value) {
  try { const url = new URL(value); return url.protocol === 'https:' ? url.href : null; }
  catch { return null; }
}

export function bookingMessage(error, fallback) {
  const status = error?.response?.status;
  if (status === 401) return 'Your sign-in expired. Verify your phone again.';
  if (status === 429) return 'Too many attempts. Wait before trying again.';
  if (status === 409) return 'Availability changed. The schedule has been refreshed; choose an available class.';
  if (status === 403) return 'You cannot book right now. Check your available sessions or contact the gym.';
  return fallback;
}


export function gymTimezoneLabel(timezone) {
  return new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'longGeneric' })
    .formatToParts(new Date()).find(part => part.type === 'timeZoneName')?.value || timezone;
}

export function classAvailability(item) {
  if (item.enrolled) return { text: 'Booked', tone: 'status' };
  if (item.waitlisted) return { text: 'On waitlist', tone: 'status' };
  if (item.spotsLeft <= 0) return { text: item.waitlistSpotsLeft === 0 ? 'Waitlist full' : 'Full', tone: 'status' };
  if (item.spotsLeft <= 4) return { text: `${item.spotsLeft} ${item.spotsLeft === 1 ? 'spot' : 'spots'} left`, tone: 'limited' };
  return { text: '', tone: '' };
}


export function waitlistConfirmations(previous, current) {
  const waiting = new Set(previous.filter(item => item.waitlisted).map(item => item.id));
  return current.filter(item => item.enrolled && waiting.has(item.id));
}

export function waitlistDepartures(previous, current) {
  const latest = new Map(current.map(item => [item.id, item]));
  return previous.filter(item => item.waitlisted && !latest.get(item.id)?.waitlisted && !latest.get(item.id)?.enrolled);
}

export function bookingDepartures(previous, current) {
  const latest = new Map(current.map(item => [item.id, item]));
  return previous.filter(item => item.enrolled && !latest.get(item.id)?.enrolled);
}


export function nextAvailabilityBoundary(classes, timezone, cutoffMinutes, now = Date.now()) {
  const format = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' });
  const today = format.format(new Date(now));
  let low = now, high = now + 36 * 3600000;
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (format.format(new Date(middle)) === today) low = middle; else high = middle;
  }
  let boundary = high;
  for (const item of classes) {
    const start = new Date(item.start).getTime();
    if (start > now) boundary = Math.min(boundary, start);
    const cutoff = start - cutoffMinutes * 60000;
    if (item.canWaitlist && cutoff > now) boundary = Math.min(boundary, cutoff);
  }
  return boundary;
}
